import React, { useLayoutEffect, useId, useMemo, useRef } from 'react';
import { renderPromotionContent, renderGreetingClosingContent } from '../../utils/promotionContentUtils';
import { buildContentContrastRules } from '../../utils/contentContrast';
import { buildContentFontSizeRules } from '../../utils/contentFontSize';
import '../../utils/promotionContentStyles.css';

export type PromotionTextSize = 'normal' | 'large' | 'xlarge';

interface PromotionHtmlProps {
  content: string;
  kind?: 'body' | 'greeting';
  textSize?: PromotionTextSize;
}

/** 읽기 전용 콘텐츠의 변환 결과와 표시 대비를 소유한다. Firestore 원문에는 관여하지 않는다. */
export default function PromotionHtml({ content, kind = 'body', textSize = 'normal' }: PromotionHtmlProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const id = `promotion-html-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  // 변환 후 정화된 결과는 내용/종류가 바뀔 때만 갱신한다. 글자 크기 변경은 DOM을 재삽입하지 않는다.
  const html = useMemo(() => kind === 'body' ? renderPromotionContent(content) : renderGreetingClosingContent(content), [content, kind]);
  const innerHtml = useMemo(() => ({ __html: html }), [html]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)');
    const style = document.createElement('style');
    document.head.appendChild(style);
    const update = () => {
      if (style.sheet) style.sheet.disabled = true;
      try {
        const selector = `.promotion-theme #${id}`;
        const contrastRules = systemTheme.matches
          ? buildContentContrastRules(root, selector, 'var(--promotion-content-foreground)') : '';
        style.textContent = [buildContentFontSizeRules(root, selector), contrastRules].filter(Boolean).join('\n');
      } finally {
        if (style.sheet) style.sheet.disabled = false;
      }
    };
    update();
    systemTheme.addEventListener('change', update);
    window.addEventListener('resize', update);
    return () => {
      systemTheme.removeEventListener('change', update);
      window.removeEventListener('resize', update);
      style.remove();
    };
  }, [html, id, textSize]);

  return <div ref={rootRef} id={id} className={`promotion-html promotion-text--${textSize} prose prose-${textSize}`} dangerouslySetInnerHTML={innerHtml} />;
}
